import { useState, useEffect } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { motion } from "framer-motion";
import axios from "axios";
import Layout from "../components/Layout";
import { ContentActionsMenu, EditedLabel } from "../components/ContentActionsMenu";
import { ThreadItem } from "../components/ThreadItem";
import { ArrowLeft, MessageCircle, Send, EyeOff } from "lucide-react";

const DiscussionDetailPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [discussion, setDiscussion] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newReply, setNewReply] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [replyAnon, setReplyAnon] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ title: "", content: "" });
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    fetchDiscussion();
  }, [id]);

  const fetchDiscussion = async () => {
    try {
      const response = await axios.get(`${API}/discussions/${id}`);
      const d = response.data.discussion;
      setDiscussion(d);
      setReplies(response.data.replies);
      if (searchParams.get("edit") === "1" && d.can_manage) {
        setEditForm({ title: d.title, content: d.content });
        setEditing(true);
        setSearchParams({}, { replace: true });
      }
    } catch (error) {
      toast.error("Discussion not found");
      navigate("/discussions");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReply = async (e) => {
    e.preventDefault();
    if (!newReply.trim()) return;

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/discussions/${id}/replies`, {
        content: newReply,
        is_anonymous: replyAnon,
      });
      setReplies([...replies, response.data]);
      setNewReply("");
      toast.success(response.data.is_anonymous ? `Reply posted as ${response.data.author_name}` : "Reply posted!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to post reply");
    } finally {
      setSubmitting(false);
    }
  };

  const saveDiscussion = async () => {
    setSavingEdit(true);
    try {
      const res = await axios.put(`${API}/discussions/${id}`, editForm);
      setDiscussion((prev) => ({ ...prev, ...res.data }));
      setEditing(false);
      res.data.message ? toast.warning(res.data.message) : toast.success("Discussion updated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update");
    } finally { setSavingEdit(false); }
  };

  const deleteDiscussion = async () => {
    try {
      await axios.delete(`${API}/discussions/${id}`);
      toast.success("Discussion deleted");
      navigate("/discussions");
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to delete"); }
  };

  const saveReply = async (reply, content) => {
    try {
      const res = await axios.put(`${API}/discussions/${id}/replies/${reply.reply_id}`, { content });
      setReplies((prev) => prev.map((r) => r.reply_id === reply.reply_id ? { ...r, ...res.data } : r));
      toast.success("Reply updated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update");
      throw error;
    }
  };

  const deleteReply = async (reply) => {
    try {
      await axios.delete(`${API}/discussions/${id}/replies/${reply.reply_id}`);
      setReplies((prev) => prev.filter((r) => r.reply_id !== reply.reply_id));
      toast.success("Reply deleted");
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to delete"); }
  };

  const forcedAnon = !!discussion?.viewer_is_anonymous_op;

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-CA", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6 md:p-8 lg:p-12">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-[#F0ECF7] rounded w-1/4"></div>
            <div className="h-12 bg-[#F0ECF7] rounded w-3/4"></div>
            <div className="h-32 bg-[#F0ECF7] rounded"></div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12" data-testid="discussion-detail-page">
        {/* Back button */}
        <Link 
          to="/discussions" 
          className="inline-flex items-center text-[#64748B] hover:text-[#6B4E8A] mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Discussions
        </Link>

        {/* Discussion */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative bg-white border border-[#E5DFEC] rounded-2xl p-6 md:p-8 mb-8"
        >
          <div className="absolute top-4 right-4">
            <ContentActionsMenu
              testIdPrefix="discussion-detail"
              canManage={discussion?.can_manage}
              reportType="discussion"
              reportId={discussion?.discussion_id}
              revealType={discussion?.is_anonymous ? "discussion" : undefined}
              revealId={discussion?.discussion_id}
              onEdit={() => { setEditForm({ title: discussion.title, content: discussion.content }); setEditing(true); }}
              onDelete={deleteDiscussion}
              deleteTitle="Delete this discussion?"
              deleteDescription="This permanently removes the discussion and all of its replies. This cannot be undone."
            />
          </div>
          {editing ? (
            <div className="space-y-3 mb-6 pr-10" data-testid="discussion-edit-form">
              <Input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} className="bg-[#FAF7F2] border-[#E5DFEC] text-lg" data-testid="discussion-edit-title" />
              <Textarea value={editForm.content} onChange={(e) => setEditForm({ ...editForm, content: e.target.value })} className="bg-[#FAF7F2] border-[#E5DFEC] min-h-[160px]" data-testid="discussion-edit-content" />
              <div className="flex justify-end gap-2">
                <Button variant="outline" className="rounded-full" onClick={() => setEditing(false)} data-testid="discussion-edit-cancel">Cancel</Button>
                <Button className="rounded-full bg-[#6B4E8A] text-white hover:bg-[#553D6F]" disabled={savingEdit || !editForm.title.trim() || !editForm.content.trim()} onClick={saveDiscussion} data-testid="discussion-edit-save">
                  {savingEdit ? "Saving..." : "Save changes"}
                </Button>
              </div>
            </div>
          ) : (
          <>
          <h1 className="text-2xl md:text-3xl font-medium text-[#6B4E8A] mb-4 pr-10" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {discussion?.title}
          </h1>
          
          <div className="flex flex-wrap items-center gap-3 mb-6">
            {discussion?.tags?.map((tag) => (
              <span key={tag} className="px-3 py-1 bg-[#F0ECF7] text-[#6B4E8A] text-xs font-medium rounded-full">
                {tag}
              </span>
            ))}
            {discussion?.province_tags?.map((p) => (
              <span key={p} className="px-3 py-1 bg-[#F0ECF7] text-[#6B4E8A] text-xs font-medium rounded-full">
                {p}
              </span>
            ))}
          </div>

          <p className="text-[#475569] whitespace-pre-wrap leading-relaxed mb-6">
            {discussion?.content}
          </p>
          </>
          )}

          <div className="flex items-center gap-4 pt-6 border-t border-[#E5DFEC]">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-[#8DB5A0] text-white flex items-center justify-center font-medium">
                {discussion?.is_anonymous ? "?" : (discussion?.author_name?.[0] || 'U')}
              </div>
              <div>
                <p className="font-medium text-[#6B4E8A]" data-testid="discussion-author">{discussion?.author_name}</p>
                <p className="text-xs text-[#64748B]">{formatDate(discussion?.created_at)} <EditedLabel editedAt={discussion?.edited_at} testId="discussion-edited" /></p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Replies Section */}
        <div className="mb-8">
          <h2 className="text-lg font-medium text-[#6B4E8A] mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <MessageCircle className="w-5 h-5" />
            {replies.length} {replies.length === 1 ? 'Reply' : 'Replies'}
          </h2>

          {replies.length > 0 ? (
            <div className="space-y-4">
              {replies.map((reply, idx) => (
                <motion.div
                  key={reply.reply_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="relative bg-white border border-[#E5DFEC] rounded-2xl p-5"
                >
                  <ThreadItem
                    item={reply}
                    idKey="reply_id"
                    reportType="discussion_reply"
                    testIdPrefix="reply"
                    onSave={saveReply}
                    onDelete={deleteReply}
                    formatDate={formatDate}
                  />
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="bg-[#FAF7F2] border border-[#E5DFEC] rounded-2xl p-8 text-center">
              <p className="text-[#64748B]">No replies yet. Be the first to share your thoughts!</p>
            </div>
          )}
        </div>

        {/* Reply Form */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white border border-[#E5DFEC] rounded-2xl p-6"
        >
          <h3 className="font-medium text-[#6B4E8A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Add a Reply
          </h3>
          <form onSubmit={handleSubmitReply}>
            <Textarea
              value={newReply}
              onChange={(e) => setNewReply(e.target.value)}
              placeholder="Share your thoughts..."
              className="bg-[#FAF7F2] border-[#E5DFEC] focus:border-[#6B4E8A] min-h-[120px] mb-4"
              data-testid="reply-input"
            />
            <div className="flex flex-wrap items-center justify-between gap-3">
              {forcedAnon ? (
                <p className="text-xs text-[#64748B] inline-flex items-center gap-1.5" data-testid="reply-forced-anon-note">
                  <EyeOff className="w-3.5 h-3.5" /> This is your anonymous post — you'll reply as {discussion?.author_name}.
                </p>
              ) : (
                <div className="flex items-center gap-2">
                  <Checkbox id="reply-anon" checked={replyAnon} onCheckedChange={(c) => setReplyAnon(!!c)} data-testid="reply-anonymous-checkbox" />
                  <Label htmlFor="reply-anon" className="text-sm text-[#64748B] cursor-pointer">Reply anonymously</Label>
                </div>
              )}
              <Button
                type="submit"
                className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full"
                disabled={submitting || !newReply.trim()}
                data-testid="submit-reply-btn"
              >
                {submitting ? (
                  "Posting..."
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Post Reply
                  </>
                )}
              </Button>
            </div>
          </form>
        </motion.div>
      </div>
    </Layout>
  );
};

export default DiscussionDetailPage;
